import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTheme } from '../../theme/useTheme';
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions, Platform } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PerfectTruckDashboard from './PerfectTruckDashboard';
import PerfectTruckCreate, { type PerfectTruckCreateHandle } from './PerfectTruckCreate';
import PerfectTruckCheckCreate, { type PerfectTruckCheckCreateHandle } from './PerfectTruckCheckCreate';
import PerfectTruckReview, { type PerfectTruckReviewHandle } from './PerfectTruckReview';
import PTLanguageToggle from './PTLanguageToggle';
import { DeliveryListItem } from './types';
import { AdminColors } from '../Admin/constants/adminColors';
import FontFamily from '../../constants/fonts';
import LhButton from '../../components/LhButton';
import { canCreateCertification, canCreateCheck, canSubmitCheckerVerification } from './perfectTruckRoles';
import { hasPerfectTruckFullAccess } from '../harbor/harborRoles';
import store, { type RootState } from '../../store';
import { setCameFromLocationLink, setRestoreRequested } from '../../store/slices/perfectTruckAnalyticsUiSlice';
import { useDarkStyles } from '../../theme/useDarkStyles';

type PerfectTruckView = 'dashboard' | 'create' | 'create-check' | 'review';

export interface PerfectTruckScreenHandle {
  handleNavigationAttempt: (callback: () => void) => void;
}

interface PerfectTruckScreenProps {
  onBackPress?: () => void;
}

const PerfectTruckScreen = forwardRef<PerfectTruckScreenHandle, PerfectTruckScreenProps>(({ onBackPress }, ref) => {
  const styles = useDarkStyles(baseStyles);
  const { t } = useTranslation('perfectTruck');
  const [view, setView] = useState<PerfectTruckView>('dashboard');
  const [selectedRecord, setSelectedRecord] = useState<DeliveryListItem | undefined>();
  const [draftId, setDraftId] = useState<number | undefined>();
  const [viewOnly, setViewOnly] = useState(false);
  const createFormRef = useRef<PerfectTruckCreateHandle>(null);
  const createCheckFormRef = useRef<PerfectTruckCheckCreateHandle>(null);
  const reviewFormRef = useRef<PerfectTruckReviewHandle>(null);

  // Expose navigation guard to App-level (e.g. DLOSidebar) via ref
  useImperativeHandle(ref, () => ({
    handleNavigationAttempt: (callback: () => void) => {
      if (view === 'create' && createFormRef.current) {
        createFormRef.current.handleNavigationAttempt(callback);
      } else if (view === 'create-check' && createCheckFormRef.current) {
        createCheckFormRef.current.handleNavigationAttempt(callback);
      } else if (view === 'review' && reviewFormRef.current) {
        reviewFormRef.current.handleNavigationAttempt(callback);
      } else {
        callback();
      }
    },
  }));

  const moduleAccess = useSelector((state: RootState) => state.appData.moduleAccess);
  const isPerfectTruckReadOnly = moduleAccess?.find(m => m.moduleName === 'Perfect Truck')?.readOnly ?? false;
  const roleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId);
  const roleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName);
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const isMobile = Platform.OS !== 'web';
  const dispatch = useDispatch();
  const hasPTFullAccess = hasPerfectTruckFullAccess(roleName);
  const isReadOnlyAccess = !hasPTFullAccess;
  const canCreate = canCreateCertification(isPerfectTruckReadOnly) && hasPTFullAccess;

  // Captured once on mount: whether the user arrived here via a location name
  // link in the Perfect Truck Analytics Location Summary tab. Consumed
  // immediately so a later, unrelated navigation to Perfect Truck (e.g. via
  // the sidebar) doesn't incorrectly keep showing this breadcrumb.
  const [showAnalyticsBreadcrumb] = useState(
    () => !!store.getState().perfectTruckAnalyticsUi.cameFromLocationLink,
  );
  useEffect(() => {
    dispatch(setCameFromLocationLink(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateBackToPerfectTruckAnalytics = () => {
    dispatch(setRestoreRequested(true));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nav = (globalThis as any).appNavigate as ((screen: string, params?: any) => void) | undefined;
    if (nav) {
      nav('/navigator-reports/perfect-truck-analytics');
    }
  };

  // Redirect to dashboard whenever the selected location changes while on create/review
  const prevLocationId = useRef(selectedLocation?.id);
  useEffect(() => {
    if (
      prevLocationId.current !== undefined &&
      prevLocationId.current !== selectedLocation?.id &&
      (view === 'create' || view === 'create-check' || view === 'review')
    ) {
      if (view === 'create' && createFormRef.current) {
        createFormRef.current.handleNavigationAttempt(() => navigateTo('dashboard'));
      } else if (view === 'create-check' && createCheckFormRef.current) {
        createCheckFormRef.current.handleNavigationAttempt(() => navigateTo('dashboard'));
      } else if (view === 'review' && reviewFormRef.current) {
        reviewFormRef.current.handleNavigationAttempt(() => navigateTo('dashboard'));
      } else {
        navigateTo('dashboard');
      }
    }
    prevLocationId.current = selectedLocation?.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id]);

  const navigateTo = (v: PerfectTruckView, record?: DeliveryListItem, openDraftId?: number, readOnly: boolean = false) => {
    setSelectedRecord(record);
    setDraftId(openDraftId);
    setViewOnly(readOnly);
    setView(v);
  };

  // Title/breadcrumb label for the create view — depends on view-only / draft state
  const createTitle = viewOnly
    ? t('breadcrumb_view_cert')
    : draftId
      ? t('breadcrumb_continue_cert')
      : t('breadcrumb_new_cert');

  const renderHeader = () => {
    if (view === 'dashboard') {
      return (
        <View style={[styles.header, isMobile && styles.headerMobile]}>
          <View style={[styles.breadcrumbRow, isMobile && styles.breadcrumbRowMobile]}>
            {showAnalyticsBreadcrumb ? (
              <View style={[styles.breadcrumb, styles.breadcrumbConstrained]}>
                <TouchableOpacity onPress={navigateBackToPerfectTruckAnalytics} testID="pt-breadcrumb-analytics-link">
                  <Text style={styles.breadcrumbLink} numberOfLines={1} ellipsizeMode="tail">
                    {t('breadcrumb_analytics')}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSep}>›</Text>
                <Text style={styles.breadcrumbCurrent} numberOfLines={1} ellipsizeMode="tail">
                  {t('breadcrumb_perfect_truck')}
                </Text>
              </View>
            ) : (
              <View style={styles.breadcrumb}>
                <TouchableOpacity onPress={onBackPress}>
                  <Text style={styles.breadcrumbLink}>{t('breadcrumb_harbor')}</Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSep}>›</Text>
                <Text style={styles.breadcrumbCurrent}>{t('breadcrumb_perfect_truck')}</Text>
              </View>
            )}
            {!isMobile && <PTLanguageToggle />}
          </View>
          <View style={[styles.titleRow, isMobile && styles.titleRowMobile]}>
            <Text style={styles.pageTitle}>{t('page_title')}</Text>
            {isMobile && <PTLanguageToggle compact />}
            {!isMobile && (canCreateCertification(isPerfectTruckReadOnly) || canCreateCheck(roleId)) && (
              <View style={styles.headerActions}>
                {canCreateCertification(isPerfectTruckReadOnly) && (
                  <LhButton
                    text={t('breadcrumb_new_cert')}
                    variant="primary"
                    size="small"
                    iconLeading="add"
                    style={styles.headerActionBtn}
                    onPress={() => navigateTo('create')}
                  />
                )}
                {canCreateCheck(roleId) && (
                  <LhButton
                    text={t('breadcrumb_new_check')}
                    variant="primary"
                    size="small"
                    iconLeading="add"
                    style={styles.headerActionBtn}
                    onPress={() => navigateTo('create-check')}
                  />
                )}
              </View>
            )}
          </View>
        </View>
      );
    }
    if (view === 'create') {
      return (
        <View style={[styles.header, isMobile && styles.headerMobile]}>
          <View style={[styles.breadcrumbRow, { justifyContent: 'space-between' }]}>
            <View style={styles.breadcrumb}>
              <TouchableOpacity onPress={() => {
                if (createFormRef.current) {
                  createFormRef.current.handleNavigationAttempt(() => navigateTo('dashboard'));
                } else {
                  navigateTo('dashboard');
                }
              }}>
                <Text style={styles.breadcrumbLink}>{t('page_title')}</Text>
              </TouchableOpacity>
              <Text style={styles.breadcrumbSep}>›</Text>
              <Text style={styles.breadcrumbCurrent}>{createTitle}</Text>
            </View>
            {!isMobile && <PTLanguageToggle />}
          </View>
          <View style={[styles.titleRow, isMobile && styles.titleRowMobile]}>
            <Text style={styles.pageTitle}>{createTitle}</Text>
            {isMobile && <PTLanguageToggle />}
          </View>
        </View>
      );
    }
    if (view === 'review') {
      return (
        <View style={[styles.header, isMobile && styles.headerMobile]}>
          <View style={styles.breadcrumbRow}>
            <View style={styles.breadcrumb}>
              <TouchableOpacity onPress={() => {
                if (reviewFormRef.current) {
                  reviewFormRef.current.handleNavigationAttempt(() => navigateTo('dashboard'));
                } else {
                  navigateTo('dashboard');
                }
              }}>
                <Text style={styles.breadcrumbLink}>{t('page_title')}</Text>
              </TouchableOpacity>
              <Text style={styles.breadcrumbSep}>›</Text>
              <Text style={styles.breadcrumbCurrent}>{t('breadcrumb_review', { route: (selectedRecord ? (selectedRecord.deliveryType === 'WHD' ? `BOL# ${selectedRecord.routeId}` : `Route ${selectedRecord.routeId}`) : t('breadcrumb_record_fallback')) })}</Text>
            </View>
            <PTLanguageToggle />
          </View>
        </View>
      );
    }
    return null;
  };

  const renderCreateCheckHeader = () => (
    <View style={[styles.header, isMobile && styles.headerMobile]}>
      <View style={styles.breadcrumb}>
        <TouchableOpacity onPress={() => {
          if (createCheckFormRef.current) {
            createCheckFormRef.current.handleNavigationAttempt(() => navigateTo('dashboard'));
          } else {
            navigateTo('dashboard');
          }
        }}>
          <Text style={styles.breadcrumbLink}>Perfect Truck</Text>
        </TouchableOpacity>
        <Text style={styles.breadcrumbSep}>›</Text>
        <Text style={styles.breadcrumbCurrent}>{t('breadcrumb_new_check')}</Text>
      </View>
      {!isMobile && <Text style={styles.pageTitle}>{t('breadcrumb_new_check')}</Text>}
    </View>
  );

  return (
    <View style={styles.container}>
      {view === 'create-check' ? renderCreateCheckHeader() : renderHeader()}
      <View style={styles.content}>
        {view === 'dashboard' && (
          <PerfectTruckDashboard
            canCreate={canCreate}
            onCreatePress={() => navigateTo('create')}
            onRowPress={(record) => {
              if (isReadOnlyAccess) {
                if (record.status === 'Pending Check') {
                  navigateTo('create', record, record.id, true);
                  return;
                }
                navigateTo('review', record);
                return;
              }

              if (record.status === 'In Progress - Certifier' && canCreateCertification(isPerfectTruckReadOnly)) {
                // Non-Team Lead users can continue an in-progress certifier draft
                navigateTo('create', record, record.id);
              } else {
                // All users (including Team Lead) can open the review page
                navigateTo('review', record);
              }
            }}
            onViewPress={(record) => navigateTo('create', record, record.id, true)}
          />
        )}
        {view === 'create' && (
          <PerfectTruckCreate
            ref={createFormRef}
            onCancel={() => navigateTo('dashboard')}
            onSubmit={() => navigateTo('dashboard')}
            draftId={draftId}
            readOnly={viewOnly}
          />
        )}
        {view === 'create-check' && (
          <PerfectTruckCheckCreate
            ref={createCheckFormRef}
            onCancel={() => navigateTo('dashboard')}
            onSubmit={() => navigateTo('dashboard')}
          />
        )}
        {view === 'review' && (
          <PerfectTruckReview
            ref={reviewFormRef}
            record={selectedRecord}
            onBack={() => navigateTo('dashboard')}
            onApprove={() => navigateTo('dashboard')}
            onReject={() => navigateTo('dashboard')}
            forceReadOnly={isReadOnlyAccess}
          />
        )}
      </View>
    </View>
  );
});

const baseStyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  header: {
    paddingHorizontal: 28,
    paddingTop: 20,
    paddingBottom: 16,
    backgroundColor: AdminColors.bgPage,
  },
  headerMobile: {
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 12,
  },
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  breadcrumbRowMobile: { marginBottom: 8 },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  breadcrumbConstrained: {
    maxWidth: '100%',
    overflow: 'hidden',
    flexShrink: 1,
  },
  breadcrumbLink: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: AdminColors.blue700,
    flexShrink: 1,
  },
  breadcrumbSep: {
    fontSize: 12,
    color: AdminColors.gray150,
  },
  breadcrumbCurrent: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textMuted,
    flexShrink: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  // LhButton defaults to width: "20%" on web; override so text isn't clipped when two sit side by side.
  headerActionBtn: {
    width: 'auto',
    alignSelf: 'flex-start',
    paddingHorizontal: 16,
  },
  titleRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  createBtnMobile: {
    alignSelf: 'stretch',
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
  },
  pageTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 24,
    color: AdminColors.textPrimary,
  },
  content: { flex: 1 },
});

export default PerfectTruckScreen;
